import { StreamId } from "@common/domain";
import { fromEvent, Subject } from "rxjs";
import { takeUntil, switchMap, take, tap } from "rxjs/operators";
import { PlainExtendedMediaStream } from "../../extended-media-stream";
import {
  mapVideoToCanvas,
  SafeAudioContext,
  safeCaptureStream
} from "../../utils";
import { LocalConferenceStream } from "./LocalConferenceStream";

export class LocalVideoConferenceStream extends LocalConferenceStream {
  private readonly _video: HTMLVideoElement;
  private readonly _canvas: HTMLCanvasElement;
  private readonly _destroy1$ = new Subject<void>();

  constructor(
    streamId: StreamId,
    private readonly file: File,
    private readonly _videoElementFactory: () => HTMLVideoElement,
    private readonly _canvasElementFactory: () => HTMLCanvasElement,
    logger: (...args: unknown[]) => void
  ) {
    super(streamId, logger);

    this._video = this._videoElementFactory();
    this._video.src = URL.createObjectURL(this.file);
    this._video.loop = true;

    // `captureStream` approach introduces weird behaviour and isn't supported by Safari,
    // use canvas to get video stream
    this._canvas = this._canvasElementFactory();

    const stream = new PlainExtendedMediaStream(
      safeCaptureStream(this._canvas)
    );

    const audioContext = new SafeAudioContext();
    const mp3AudioSource = audioContext.createMediaElementSource(this._video);
    const mp3AudioDestination = audioContext.createMediaStreamDestination();
    mp3AudioSource.connect(mp3AudioDestination);

    mp3AudioDestination.stream
      .getAudioTracks()
      .forEach((_) => stream.addTrack(_));

    this._stream$.next(stream);

    fromEvent(this._video, "loadeddata")
      .pipe(
        take(1),
        // Update time to set initial thumbnail on first load
        tap(() => (this._video.currentTime = 0)),
        switchMap(() =>
          // Preload data to MCU
          // https://liveedev.atlassian.net/browse/YOUM-125
          mapVideoToCanvas(this._video, this._canvas).pipe(
            tap(() =>
              stream.videoTracks.forEach((_) =>
                (_ as CanvasCaptureMediaStreamTrack).requestFrame()
              )
            )
          )
        ),
        takeUntil(this._destroy1$)
      )
      .subscribe();
  }

  override async destroy(): Promise<void> {
    this._destroy1$.next();
    this._destroy1$.complete();

    await super.destroy();
  }

  override async audioDisable() {
    this._video.muted = true;

    await super.audioDisable();
  }

  override async audioEnable() {
    this._video.muted = false;

    await super.audioEnable();
  }

  async play() {
    return this._video.play();
  }

  pause() {
    this._video.pause();
  }

  restart() {
    this._video.currentTime = 0;
  }
}
